import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Form, Select, InputNumber, Tooltip, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import AddHostelAdminModal from '../components/AddHostelAdminModal';
import api from '../utils/api';

const MainAdminDashboard = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/admin/owners');
      setAdmins(response.data);
    } catch (error) {
      message.error('Failed to load owners');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminUpdate = async (values) => {
    try {
      const updateData = {};
      if (values.subscriptionStatus) updateData.subscriptionStatus = values.subscriptionStatus;
      if (values.maxHostelsAllowed != null) updateData.maxHostelsAllowed = values.maxHostelsAllowed;
      await api.patch(`/admin/owners/${selectedAdmin._id}/access`, updateData);
      message.success('Owner updated');
      fetchAdmins();
      setManageModalOpen(false);
      setSelectedAdmin(null);
      form.resetFields();
    } catch (error) {
      const errMsg = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Update failed';
      message.error(errMsg);
    }
  };

  const handleDeleteAdmin = (admin) => {
    Modal.confirm({
      title: `Delete owner "${admin.name}"?`,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.delete(`/admin/owners/${admin._id}`);
          message.success('Owner deleted');
          fetchAdmins();
        } catch (error) {
          message.error(error.response?.data?.message || 'Delete failed');
        }
      },
    });
  };

  const openManageModal = (admin) => {
    setSelectedAdmin(admin);
    form.setFieldsValue({
      subscriptionStatus: admin.subscriptionStatus === 'active' ? 'active' : 'suspended',
      maxHostelsAllowed: admin.maxHostelsAllowed ?? 10,
    });
    setManageModalOpen(true);
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (t) => <strong>{t}</strong> },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Status',
      dataIndex: 'subscriptionStatus',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>{status === 'active' ? 'Active' : 'Suspended'}</Tag>
      ),
    },
    {
      title: 'Hostels',
      key: 'hostels',
      render: (_, r) => `${r.hostelCount || 0} / ${r.maxHostelsAllowed || 10}`,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Manage">
            <Button type="link" icon={<EditOutlined />} onClick={() => openManageModal(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteAdmin(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loading) return null;

  return (
    <>
      <Card
        title={<span style={{ fontSize: 20, fontWeight: 600 }}>Super Admin Dashboard</span>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
            Add Owner
          </Button>
        }
      >
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={admins}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={selectedAdmin ? `Manage: ${selectedAdmin.name}` : 'Manage Owner'}
        open={manageModalOpen}
        onCancel={() => { setManageModalOpen(false); setSelectedAdmin(null); form.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        {selectedAdmin && (
          <Form form={form} layout="vertical" onFinish={handleAdminUpdate}>
            <Form.Item name="subscriptionStatus" label="Subscription Status" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'suspended', label: 'Suspended' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="maxHostelsAllowed"
              label="Maximum Hostels Allowed"
              rules={[{ required: true }]}
              extra={`Current: ${selectedAdmin.hostelCount || 0} hostel(s). Minimum: ${selectedAdmin.hostelCount || 0}`}
            >
              <InputNumber min={selectedAdmin.hostelCount || 0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">Update</Button>
                <Button onClick={() => setManageModalOpen(false)}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {addModalOpen && (
        <AddHostelAdminModal
          onClose={() => setAddModalOpen(false)}
          onSuccess={() => { fetchAdmins(); setAddModalOpen(false); }}
        />
      )}
    </>
  );
};

export default MainAdminDashboard;
